import StreamsBatchDialog from "../../components/coordinator/StreamsBatchDialog.jsx";
import React, { useState } from "react";
import { usePageTitle } from "../../lib/brand.js";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth.jsx";
import { useIsCompact } from "../../hooks/useIsCompact.js";
import UserChip from "../../components/UserChip.jsx";
import ClientBreadcrumb from "../../components/ClientBreadcrumb.jsx";
import { useApi } from "../../hooks/useApi.js";
import LoadingSpinner from "../../components/LoadingSpinner.jsx";
import ErrorBanner from "../../components/ErrorBanner.jsx";
import PlaythroughPanel from "../../components/coordinator/PlaythroughPanel.jsx";
import ImpressionsPanel from "../../components/coordinator/ImpressionsPanel.jsx";
import EngagementPanel from "../../components/coordinator/EngagementPanel.jsx";
import NoticedPanel, { NOTICED_COPY } from "../../components/coordinator/NoticedPanel.jsx";
import CollapsiblePanel from "../../components/CollapsiblePanel.jsx";
import InterviewsPanel from "../../components/coordinator/InterviewsPanel.jsx";
import AddMaterialCard from "../../components/coordinator/AddMaterialCard.jsx";
import AccessPanel from "../../components/AccessPanel.jsx";
import AuthoringNotePanel, { AUTHORING_COPY } from "../../components/coordinator/AuthoringNotePanel.jsx";
import { sectionLabel } from "../../lib/sections.js";

const SECTION_ORDER = [
  "meet", "communicate", "sensory", "passions", "day",
  "working_on", "hard_times", "health", "people", "community",
];


const STATUS_COLORS = {
  live:  { color: "var(--color-status-live)",  bg: "var(--color-accent-subtle)" },
  draft: { color: "var(--color-status-draft)", bg: "#fef3e2" },
  empty: { color: "var(--color-status-empty)", bg: "var(--color-bg-subtle)" },
};

// A section's play-through, as a small chip on its tile (Oct 20). Present only
// when the server sends section_playthroughs (the feature is switched on).
function playthroughChip(status) {
  if (!status) return null;
  if (status === "ready") return { label: "▶ Ready", color: "var(--color-status-live)", bg: "var(--color-accent-subtle)" };
  if (status === "failed" || status === "plan_failed") return { label: "▶ Failed", color: "var(--color-critical)", bg: "var(--color-critical-bg)" };
  return { label: "▶ Draft", color: "#7c3aed", bg: "rgba(139,92,246,0.08)" };
}

const TYPE_TO_SLUG = {
  meet: "meet", communicate: "communicate", sensory: "sensory",
  passions: "passions", day: "day", working_on: "working-on",
  hard_times: "hard-times", health: "health", people: "people",
  community: "community",
};

export default function ClientEditorHub() {
  const { clientId } = useParams();
  const { user, logout } = useAuth();
  const compact = useIsCompact();
  const navigate = useNavigate();
  const [showReset, setShowReset] = useState(false);
  const [batch, setBatch] = useState(null);       // "plan" | "author" (Oct 28)
  async function handleLogout() { await logout(); navigate("/login", { replace: true }); }
  const { data: profile, loading, error, refetch } = useApi(`/clients/${clientId}/profile`);
  // Whether an authoring note is set, once saved here — without refetching
  // the profile (which would swap the hub for a spinner).
  const [noteSet, setNoteSet] = useState(null);
  const { data: pending, refetch: refetchPending } = useApi(`/clients/${clientId}/pending-review`);
  const { data: noticedCount, refetch: refetchNoticed } = useApi(`/clients/${clientId}/notes/inbox?count=1`);
  usePageTitle(profile?.display_name);

  if (loading) return <LoadingSpinner message="Loading…" />;

  // ── Reset modal ──────────────────────────────────────────────────────────
  async function doReset(scope) {
    const res = await fetch(`/api/clients/${clientId}/reset`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ scope }),
    });
    let data = null;
    try { data = await res.json(); } catch { data = null; }
    setShowReset(false);
    refetch();
    if (scope === "rebuild") {
      // Re-extract already started a background extraction on the ISP. Land
      // Quick Start on its Processing step for that document (?doc_id=), not
      // on Upload — from Upload a second "Process" click started a second
      // extraction of the same document in parallel (David, Oct 14).
      const docId = data?.isp_document_id;
      navigate(`/coordinator/clients/${clientId}/quick-start${docId ? `?doc_id=${encodeURIComponent(docId)}` : ""}`);
    }
  }

  if (error)   return <ErrorBanner message={error} onRetry={refetch} />;
  if (!profile) return null;

  const sections = [...(profile.sections || [])].sort(
    (a, b) => SECTION_ORDER.indexOf(a.section_type) - SECTION_ORDER.indexOf(b.section_type)
  );

  return (
    <div style={styles.page}>
      <div style={styles.content}>

        {/* Header: who, then one bar of same-height controls, primary on the
            right. The old line mixed a user chip, an emoji link, bare text
            buttons and a badge link in four sizes. The user chip lives in the
            breadcrumb row like every other page; Reset and Quick Start — rare
            and destructive-adjacent — sit at the bottom under a rule. */}
        <div style={styles.crumbRow}>
          <ClientBreadcrumb clientName={profile.display_name} role={user?.role} />
          <UserChip user={user} onLogout={handleLogout} />
        </div>
        <div style={{ ...styles.topBar, ...(compact ? styles.topBarCompact : {}) }}>
          <h1 style={styles.name}>{profile.display_name}</h1>
          <div style={styles.actions} role="toolbar" aria-label="Client actions">
            <Link to={`/coordinator/clients/${clientId}/media`} style={styles.chip}>Media library</Link>
            {pending?.pending && (
              <Link to={`/coordinator/clients/${clientId}/review`} style={{ ...styles.chip, ...styles.chipAccent }}>
                Review <span style={styles.reviewBadge}>{pending.counts.total}</span>
              </Link>
            )}
            {/* An outline chip like its neighbours: a preview is not the
                page's main action, and a green fill said it was (Plan E5). */}
            <Link to={`/clients/${clientId}?preview=1`} style={styles.chip} target="_blank" rel="noopener noreferrer">
              Preview as caregiver ↗
            </Link>
          </div>
        </div>

        {/* Plan all / Author all (Oct 28): two steps, review between. */}
        <div style={styles.batchRow}>
          {profile.section_playthroughs !== undefined && (
            <button type="button" style={styles.chip} onClick={() => setBatch("plan")}>Plan all sections…</button>
          )}
          <button type="button" style={styles.chip} onClick={() => setBatch("author")}>Author all streams…</button>
        </div>
        {batch && (
          <StreamsBatchDialog clientId={clientId} mode={batch} pronouns={profile.pronouns}
            onClose={() => { setBatch(null); refetch(); }} />
        )}

        <div style={styles.grid}>
          {sections.map(section => {
            const st = STATUS_COLORS[section.status] || STATUS_COLORS.empty;
            const slug = TYPE_TO_SLUG[section.section_type];
            const chip = playthroughChip(profile.section_playthroughs?.[section.section_type]);
            return (
              <Link
                key={section.id}
                to={`/coordinator/clients/${clientId}/edit/${slug}`}
                style={styles.card}
              >
                <div style={styles.cardTop}>
                  <span style={styles.cardLabel}>
                    {sectionLabel(section.section_type, profile.pronouns)}
                  </span>
                  <span style={{ ...styles.statusBadge, color: st.color, background: st.bg }}>
                    {section.status}
                  </span>
                </div>
                {section.tile_teaser && (
                  <p style={styles.teaser}>{section.tile_teaser}</p>
                )}
                {chip && (
                  <span style={{ ...styles.ptChip, color: chip.color, background: chip.bg }}
                        title="This section's stream">{chip.label}</span>
                )}
              </Link>
            );
          })}
        </div>

        {/* Direction for every stream PE authors (Nov 1), below
            the sections (Nov 2). "Set" shows when
            there is one, so it is never forgotten. */}
        <CollapsiblePanel id="authoring-note" scope={clientId} title={AUTHORING_COPY.title}
                          count={(noteSet ?? !!profile.authoring_note) ? "Set" : null}>
          <AuthoringNotePanel clientId={clientId} initial={profile.authoring_note} onSaved={v => setNoteSet(!!v)} />
        </CollapsiblePanel>

        <PlaythroughPanel clientId={clientId} />

        <CollapsiblePanel id="impressions" scope={clientId} title="Impressions">
          <ImpressionsPanel clientId={clientId} clientName={profile.display_name} />
        </CollapsiblePanel>

        {/* Permission beside behaviour, two cards (David, Oct 11): "can see"
            answers who is allowed; "using" answers who actually did. */}
        <CollapsiblePanel id="access" scope={clientId} title="Who can see this">
          <AccessPanel inline client={{ id: clientId, display_name: profile.display_name }} />
        </CollapsiblePanel>

        {/* What the team noticed (Getting Good, Nov 2026): the count is what's
            new — shared and not yet reviewed. */}
        <CollapsiblePanel id="noticed" scope={clientId} title={NOTICED_COPY.title}
                          count={noticedCount?.count ? noticedCount.count : null}>
          <NoticedPanel clientId={clientId} pronouns={profile.pronouns} onChanged={refetchNoticed} />
        </CollapsiblePanel>

        <CollapsiblePanel id="engagement" scope={clientId} title="Who's using this profile">
          <EngagementPanel clientId={clientId} pronouns={profile.pronouns} />
        </CollapsiblePanel>

        <CollapsiblePanel id="interviews" scope={clientId} title="Interviews">
          <InterviewsPanel clientId={clientId} clientName={profile.display_name} />
        </CollapsiblePanel>

        {/* The backend has taken additional documents since Quick Start shipped
            (POST /documents/additional + incremental process-all → Review), but
            nothing in the UI reached it once the Quick Start link went away.
            Hidden while the client is still in Quick Start, which has its own
            uploader (David, Oct 14). */}
        {!profile.is_quick_start && (
          <CollapsiblePanel id="add-documents" scope={clientId} title="Add documents">
            <AddMaterialCard clientId={clientId} documentsOnly onProcessed={refetchPending} />
          </CollapsiblePanel>
        )}

        {/* Rare, consequential: kept, but out of the way. */}
        <div style={styles.footerTools}>
          {profile.is_quick_start ? (
            <Link to={`/coordinator/clients/${clientId}/quick-start`} style={styles.footerLink}>Quick Start</Link>
          ) : (
            <button type="button" style={styles.footerLink} onClick={() => setShowReset(true)}>Start fresh…</button>
          )}
        </div>

      </div>


      {showReset && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:200 }}
          onClick={e => e.target===e.currentTarget && setShowReset(false)}>
          <div style={{ background:"var(--color-surface)", borderRadius:"var(--radius-xl)", padding:"var(--space-8)", width:420, display:"flex", flexDirection:"column", gap:"var(--space-4)" }}>
            <h2 style={{ fontSize:"var(--text-lg)", fontWeight:"var(--weight-semibold)" }}>Start fresh with {profile.display_name}</h2>
            <p style={{ fontSize:"var(--text-sm)", color:"var(--color-text-secondary)" }}>
              Choose an option below. This can't be undone. Photos, access permissions, and DSP history are kept in all cases.
            </p>
            <button style={styles.resetOption} onClick={() => { if (window.confirm(`Re-extract ${profile.display_name}'s profile? Everything in the sections and stream is deleted and rebuilt from the uploaded documents.`)) doReset("rebuild"); }}>
              <strong>Re-extract</strong>
              <span style={styles.resetDetail}>Deletes everything in the sections and stream. Keeps the uploaded documents and re-extracts from them to create fresh section contents.</span>
            </button>
            <button style={styles.resetOption} onClick={() => { if (window.confirm(`Empty ${profile.display_name}'s profile to prepare for document changes? Everything in the sections and stream is deleted; the documents are kept.`)) doReset("clear"); }}>
              <strong>Prep for document changes</strong>
              <span style={styles.resetDetail}>Deletes everything in the sections and stream. Keeps the uploaded documents, and lets you add or delete documents before re-extracting.</span>
            </button>
            <button style={{ ...styles.resetOption, color:"var(--color-critical)", borderColor:"var(--color-critical)" }}
              onClick={() => { if (window.confirm(`Delete all of ${profile.display_name}'s profile content? Everything in the sections and stream, and the uploaded documents, are deleted.`)) doReset("full"); }}>
              <strong>Delete all</strong>
              <span style={{ ...styles.resetDetail, color:"inherit" }}>Deletes everything in the sections and stream. Deletes the uploaded documents.</span>
            </button>
            <button style={{ background:"none", border:"none", color:"var(--color-text-tertiary)", cursor:"pointer", alignSelf:"flex-end" }}
              onClick={() => setShowReset(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { minHeight: "100dvh", background: "var(--color-bg-subtle)", padding: "var(--space-6)" },
  content: { maxWidth: "var(--content-max-width)", margin: "0 auto", display: "flex", flexDirection: "column", gap: "var(--space-8)" },
  crumbRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--space-4)", marginBottom: "calc(-1 * var(--space-4))" },
  topBar: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--space-6)", flexWrap: "wrap" },
  topBarCompact: { alignItems: "flex-start", flexDirection: "column", gap: "var(--space-3)" },
  actions: { display: "flex", alignItems: "center", gap: "var(--space-2)", flexWrap: "wrap" },
  // Links and buttons share one box: same font, weight, line-height and
  // height, so a row of them reads as one toolbar.
  chip: { font: "inherit", fontSize: "var(--text-sm)", fontWeight: "var(--weight-regular, 400)", lineHeight: 1, height: 36, boxSizing: "border-box", padding: "0 var(--space-4)", display: "inline-flex", alignItems: "center", gap: 6, border: "1px solid var(--color-border-strong)", borderRadius: "var(--radius-md)", background: "var(--color-surface)", color: "var(--color-text-secondary)", textDecoration: "none", cursor: "pointer", whiteSpace: "nowrap" },
  batchRow: { display: "flex", flexWrap: "wrap", gap: "var(--space-2)", justifyContent: "flex-end" },
  chipAccent: { color: "var(--color-accent)", borderColor: "var(--color-accent)" },
  footerTools: { borderTop: "1px solid var(--color-border)", paddingTop: "var(--space-4)", display: "flex", gap: "var(--space-5)" },
  footerLink: { font: "inherit", fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)", background: "none", border: "none", padding: 0, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 },
  badge: { fontSize: "var(--text-xs)", fontWeight: "var(--weight-semibold)", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-text-tertiary)", marginBottom: "var(--space-1)" },
  name: { fontSize: "var(--text-2xl)", fontWeight: "var(--weight-bold)", color: "var(--color-text-primary)" },
  reviewBadge: { fontSize: "var(--text-xs)", fontWeight: "var(--weight-semibold)", color: "#fff", background: "var(--color-accent)", borderRadius: "var(--radius-full)", padding: "1px 7px", lineHeight: 1.5 },
  resetOption:  { fontSize:"var(--text-sm)", background:"none", border:"1px solid var(--color-border)", borderRadius:"var(--radius-md)", padding:"var(--space-3) var(--space-4)", cursor:"pointer", textAlign:"left", display:"flex", flexDirection:"column", gap:"var(--space-1)", color:"var(--color-text-primary)" },
  resetDetail:  { fontSize:"var(--text-xs)", color:"var(--color-text-secondary)", lineHeight:"var(--leading-normal)" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "var(--space-4)" },
  card: { background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: "var(--space-5)", textDecoration: "none", display: "flex", flexDirection: "column", gap: "var(--space-2)" },
  cardTop: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--space-2)" },
  cardLabel: { fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)", color: "var(--color-text-primary)" },
  statusBadge: { fontSize: "var(--text-xs)", fontWeight: "var(--weight-medium)", borderRadius: "var(--radius-full)", padding: "2px var(--space-2)", whiteSpace: "nowrap" },
  teaser: { fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)", lineHeight: "var(--leading-normal)" },
  ptChip: { alignSelf: "flex-start", fontSize: "var(--text-xs)", fontWeight: "var(--weight-medium)", borderRadius: "var(--radius-full)", padding: "2px var(--space-2)", whiteSpace: "nowrap" },
};
